import React, { useEffect, useState } from "react";
import {
  Box, Button, Flex, Table, Tbody, Td, Th, Thead, Tr, Text, useToast, Spinner, Badge,
  Alert, AlertIcon, HStack, Select, Heading, SimpleGrid, Stat, StatLabel, StatNumber,
} from "@chakra-ui/react";
import { getJobs, cancelJob, forceWave } from "../../utils/apis/agentApi";

const STATUS_COLOR: Record<string, string> = {
  queued: "gray", dispatching: "blue", assigned: "green",
  completed: "purple", unfilled: "orange", cancelled: "red",
};

/** Agent dispatch jobs board: live WhatsApp jobs, their wave state, plus
 *  cancel and force-next-wave. */
const DispatchJobs = () => {
  const [jobs, setJobs] = useState<any[]>([]);
  const [enabled, setEnabled] = useState<boolean>(true);
  const [metrics, setMetrics] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const load = async () => {
    setLoading(true);
    const res = await getJobs(statusFilter || undefined, 100);
    if (res.ok && res.data) {
      setJobs(res.data.jobs || []); setEnabled(res.data.enabled); setMetrics(res.data.metrics);
    } else toast({ title: res.status === 502 ? "Agent unreachable" : "Failed to load",
      status: "error", duration: 3000, isClosable: true });
    setLoading(false);
  };
  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line

  const act = async (fn: () => Promise<any>, ok: string) => {
    const res = await fn();
    if (res.ok) { toast({ title: ok, status: "success", duration: 2000 }); load(); }
    else toast({ title: "Action failed", status: "error", duration: 3000 });
  };

  return (
    <Box p={5}>
      {!enabled && (
        <Alert status="warning" mb={4} borderRadius="md">
          <AlertIcon />Dispatch is turned OFF (DISPATCH_ENABLED=false). Jobs below are historical.
        </Alert>
      )}
      <Flex justifyContent="space-between" mb={4} alignItems="flex-start">
        <Box>
          <Heading size="md">Dispatch jobs</Heading>
          <Text fontSize="sm" color="admin.muted">
            Jobs the agent is actively routing to providers. A customer asked, and we're
            offering the work out in waves until someone accepts.
          </Text>
        </Box>
        <HStack>
          <Select w="170px" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} placeholder="All statuses">
            {Object.keys(STATUS_COLOR).map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
          <Button colorScheme="purple" backgroundColor="#88077b" onClick={load}>Refresh</Button>
        </HStack>
      </Flex>

      {metrics && (
        <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} spacing={3} mb={5}>
          {Object.entries(metrics).map(([k, v]) => (
            <Stat key={k} p={3} borderWidth="1px" borderRadius="md" backgroundColor="admin.surface">
              <StatLabel fontSize="xs" color="admin.muted" textTransform="capitalize">
                {k.replace(/_/g, " ")}
              </StatLabel>
              <StatNumber fontSize="lg" color="admin.accent">
                {v === null || v === undefined ? "-" : String(v)}
              </StatNumber>
            </Stat>
          ))}
        </SimpleGrid>
      )}
      {loading ? <Spinner size="xl" /> : jobs.length === 0 ? (
        <Text color="admin.muted">No jobs.</Text>
      ) : (
        <Table variant="striped" size="sm">
          <Thead backgroundColor="#88077b"><Tr>
            <Th color="white">When</Th><Th color="white">Customer</Th><Th color="white">Service</Th>
            <Th color="white">Area</Th><Th color="white">Status</Th><Th color="white">Wave</Th>
            <Th color="white">Assigned</Th><Th color="white">Actions</Th>
          </Tr></Thead>
          <Tbody>
            {jobs.map((j, i) => (
              <Tr key={j.id} backgroundColor={i % 2 === 0 ? "admin.surface" : "admin.selected"}>
                <Td whiteSpace="nowrap">{j.created_at ? new Date(j.created_at).toLocaleString() : "-"}</Td>
                <Td>{j.customer_name || j.customer_phone}</Td>
                <Td>{j.service}</Td>
                <Td>{j.area || "-"}</Td>
                <Td><Badge colorScheme={STATUS_COLOR[j.status] || "gray"}>{(j.outcome || j.status).replace(/_/g, " ")}</Badge></Td>
                <Td>{j.current_wave ?? "-"}</Td>
                <Td>{j.assigned_provider_id ? "✓" : "-"}</Td>
                <Td>
                  <HStack spacing={1}>
                    {["queued", "dispatching"].includes(j.status) && (
                      <Button size="xs" colorScheme="blue"
                        onClick={() => act(() => forceWave(j.id), "Next wave sent")}>Force wave</Button>
                    )}
                    {!["completed", "cancelled"].includes(j.status) && (
                      <Button size="xs" colorScheme="red"
                        onClick={() => act(() => cancelJob(j.id), "Cancelled")}>Cancel</Button>
                    )}
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </Box>
  );
};
export default DispatchJobs;
